import type { MaterialSrc } from "common/materials/materialSrc";
import { hexToRgb } from "common/utils/colorConversion";
import { degreeToRadian } from "common/utils/math";
import { stableStringify } from "common/utils/stableStringify";
import * as THREE from "three";
import { OrthographicCamera } from "three";
import { initSharedWebGL, WebGLContextReuse } from "../activeSharedWebGL";
import { AOAlgorithm } from "../threejs/higharc/AOAlgorithm";
import { defaultRenderParamsForGPUTier } from "../THREEViewerServices";
import { designMaterialNewFromPreprocessed } from "./designMaterial";
import { designMaterialAsFlatColor } from "./designMaterialAsFlatColor";
import {
  designMaterialLayersAreFlatColor,
  normalizeSdfPatternRotation,
  sdfPeriod,
  SdfPeriodPurpose,
  usesSdf,
} from "./designMaterialParams";
import {
  designMaterialBake,
  designMaterialBakeToBlob,
  designMaterialThumbnailOrthographicCamera,
} from "./designMaterialRenderImage";
import type { DesignMaterialLoadOptions } from "./designMaterialRuntimeConfig";
import { BakeColorTextureMode, ComponentViewMode } from "./designMaterialRuntimeConfig";
import type { DesignMaterialParamsPreprocessed, LayerParamsPreprocessed } from "./designMaterialTypes";
import type { BakedColorTexture } from "./designMaterialTypes";
import type { LoadTextureFunc } from "./loadTextureFuncType";

// Limit the cache size to 100 items. We can use a large limit since this is only for testing use.
const TESTING_BAKED_COLOR_MAP_CACHE_SIZE = 100;

export function designMaterialBakesToFlatColor(params: DesignMaterialParamsPreprocessed) {
  return designMaterialLayersAreFlatColor(params) && !usesSdf(params);
}

export function designMaterialColorVariantsCanUseTemplateTexture(templateParams: DesignMaterialParamsPreprocessed) {
  function isGreyscaleColor(color: string) {
    const rgb = hexToRgb(color);
    return rgb[0] === rgb[1] && rgb[1] === rgb[2];
  }

  function isDarkGreyscaleColor(color: string) {
    const rgb = hexToRgb(color);
    return rgb[0] === rgb[1] && rgb[1] === rgb[2] && rgb[0] < 128;
  }

  function isLowIntensityGreyscaleLayer(layer: LayerParamsPreprocessed) {
    const colors = [layer.color0, layer.color1];
    if (layer.colorCount > 2) {
      colors.push(layer.color2);
    }
    if (layer.colorCount > 3) {
      colors.push(layer.color3);
    }
    const colorStrength = layer.strength * layer.colorInfluence;
    if (colors.every((color) => isGreyscaleColor(color)) && colorStrength < 0.06) {
      return true;
    }
    if (colors.every((color) => isDarkGreyscaleColor(color)) && colorStrength < 0.15 && layer.blendMode === "alpha") {
      return true;
    }
    return false;
  }

  if (
    templateParams.layers.every(
      (layer) =>
        layer.blendMode === "multiply" || // Multiply layers will apply the same way whether they're applied before the color or after the color is applied.
        layer.strength * layer.colorInfluence < 0.01 ||
        isLowIntensityGreyscaleLayer(layer),
    )
  ) {
    return true;
  }
  return false;
}

const whiteTexture = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
whiteTexture.needsUpdate = true;
export function flatColorBakedMaterial(params: DesignMaterialParamsPreprocessed): BakedColorTexture {
  const color = designMaterialAsFlatColor(params);
  return {
    // We use a placeholder texture even though we don't really need it so that the shader is the same as for the textured variant.
    texture: whiteTexture,
    color: new THREE.Color(...color),
    worldWidth: 10,
    worldHeight: 10,
    rotation: 0,
    loadedPromise: Promise.resolve(),
  };
}

const bakeCamera = new OrthographicCamera();

export function designMaterialBakeRepeatPeriodAndRotation(params: DesignMaterialParamsPreprocessed): {
  rotation: number;
  repeatPeriodX: number;
  repeatPeriodY: number;
  mainMaskTileRepeatInterval: number;
} {
  const { normalizedPatterns, rotation: sdfRotation = 0 } = normalizeSdfPatternRotation(
    params.sdf.common,
    params.sdf.patterns,
  );

  let rotation = sdfRotation;
  let repeatPeriodX = 200;
  let repeatPeriodY = 200;
  let mainMaskTileRepeatInterval = 1;

  if (params.heightMapEnabled && params.heightMapName) {
    repeatPeriodX = params.heightMapScaleU;
    repeatPeriodY = params.heightMapScaleV;
    rotation = params.heightMapRotation;
  } else if (normalizedPatterns.length > 0) {
    const { repeatPeriodX: sdfRepeatPeriodX, repeatPeriodY: sdfRepeatPeriodY } = sdfPeriod(
      params.sdf.common,
      normalizedPatterns,
      SdfPeriodPurpose.HeightMapPeriod, // We'll use the same repeat period as we use for the heightmap, it should be suitable for layers as well.
    );
    if (sdfRepeatPeriodX && sdfRepeatPeriodY) {
      mainMaskTileRepeatInterval = 2;
      repeatPeriodX = sdfRepeatPeriodX * mainMaskTileRepeatInterval;
      repeatPeriodY = sdfRepeatPeriodY * mainMaskTileRepeatInterval;
    }
  }

  return { rotation, repeatPeriodX, repeatPeriodY, mainMaskTileRepeatInterval };
}

interface BakeColorMapParams {
  params: DesignMaterialParamsPreprocessed;
  renderer: THREE.WebGLRenderer;
  loadTextureUrl: LoadTextureFunc;
  bakedColorTextureSrc?: MaterialSrc;
  hasSSFXRenderer: boolean;
  options: DesignMaterialLoadOptions;
}

async function commonBakeColorMapSetup({
  params,
  renderer,
  loadTextureUrl,
  hasSSFXRenderer,
  options,
}: BakeColorMapParams) {
  const { services } = initSharedWebGL({ reuse: WebGLContextReuse.ALWAYS_REUSE });
  const renderParams = await defaultRenderParamsForGPUTier(services);
  renderParams.renderPipelineParams = {
    ...renderParams.renderPipelineParams,
    aoAlgorithm: AOAlgorithm.NONE,
    transparentBackground: true,
  };
  const materialToBake = await designMaterialNewFromPreprocessed(
    params,
    renderer,
    loadTextureUrl,
    hasSSFXRenderer,
    undefined,
    {
      ...options,
      bakeColorTexture: BakeColorTextureMode.REPEATING_LAYERS,
      componentViewMode: ComponentViewMode.bakedColor,
    },
  );
  const { repeatPeriodX, repeatPeriodY, rotation } = designMaterialBakeRepeatPeriodAndRotation(params);
  designMaterialThumbnailOrthographicCamera(bakeCamera, {
    worldWidth: repeatPeriodX,
    worldHeight: repeatPeriodY,
    worldCenterX: repeatPeriodX * 0.5,
    worldCenterY: repeatPeriodY * 0.5,
  });
  bakeCamera.rotation.z = degreeToRadian(rotation);
  bakeCamera.updateMatrixWorld();
  return {
    material: materialToBake,
    imageRenderer: services.imageRenderer,
    renderParams,
    options: { width: 512, height: 512 },
    camera: bakeCamera,
  };
}

export async function designMaterialBakeColorMap(bakeColorMapParams: BakeColorMapParams): Promise<BakedColorTexture> {
  const bakeParams = await commonBakeColorMapSetup(bakeColorMapParams);
  return designMaterialBake(bakeParams);
}

export async function designMaterialBakeColorMapToBlob(bakeColorMapParams: BakeColorMapParams): Promise<Blob> {
  const bakeParams = await commonBakeColorMapSetup(bakeColorMapParams);
  return await designMaterialBakeToBlob(bakeParams);
}

async function bakeColorMapWithOptimizations(bakeColorMapParams: BakeColorMapParams) {
  if (designMaterialBakesToFlatColor(bakeColorMapParams.params)) {
    return flatColorBakedMaterial(bakeColorMapParams.params);
  }

  if (
    bakeColorMapParams.bakedColorTextureSrc !== undefined &&
    bakeColorMapParams.bakedColorTextureSrc.layerConfigId === undefined &&
    designMaterialColorVariantsCanUseTemplateTexture(bakeColorMapParams.params)
  ) {
    const bakeTemplateParams = {
      ...bakeColorMapParams,
      params: {
        ...bakeColorMapParams.params,
        color: "#ffffff",
      },
      bakedColorTextureSrc: undefined,
    };
    const bakeParams = await commonBakeColorMapSetup(bakeTemplateParams);
    const bakedTemplate = await designMaterialBake(bakeParams);
    return {
      texture: bakedTemplate.texture,
      color: new THREE.Color(bakeColorMapParams.params.color),
      worldWidth: bakedTemplate.worldWidth,
      worldHeight: bakedTemplate.worldHeight,
      rotation: bakedTemplate.rotation,
      loadedPromise: Promise.resolve(),
    };
  }

  return designMaterialBakeColorMap(bakeColorMapParams);
}

// Baked textures are not WebGLRenderer specific, they are CanvasTextures.
const bakedColorMapCache: { [key: string]: { promise: Promise<BakedColorTexture>; lastUsed: number } } = {};

/**
 * This is to be used only for debugging functionality, like testing baking on the material-appearance-test page.
 * It caches baked color maps in memory to avoid re-baking them.
 */
export function designMaterialCachedBakeColorMap(bakeColorMapParams: BakeColorMapParams): Promise<BakedColorTexture> {
  const key = stableStringify({ params: bakeColorMapParams.params, options: bakeColorMapParams.options });
  if (bakedColorMapCache[key] !== undefined) {
    bakedColorMapCache[key].lastUsed = Date.now();
    return bakedColorMapCache[key].promise;
  }

  // Find and delete the least recently used item from the cache
  if (Object.keys(bakedColorMapCache).length > TESTING_BAKED_COLOR_MAP_CACHE_SIZE) {
    let oldestKey = "";
    let oldestTime = Infinity;

    for (const key in bakedColorMapCache) {
      if (bakedColorMapCache[key].lastUsed < oldestTime) {
        oldestTime = bakedColorMapCache[key].lastUsed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      void bakedColorMapCache[oldestKey].promise.then((map) => map.texture?.dispose());
      delete bakedColorMapCache[oldestKey];
    }
  }

  const bakedTexturePromise = bakeColorMapWithOptimizations(bakeColorMapParams);
  bakedColorMapCache[key] = { promise: bakedTexturePromise, lastUsed: Date.now() };
  return bakedTexturePromise;
}
